import { ChevronDown } from 'lucide-react';
import { useCallback, useMemo } from 'react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useTranslation } from '@/hooks/useTranslation';

/**
 * Props interface for the ApprovalFilterDropdown component
 */
interface ApprovalFilterDropdownProps {
  /** Array of currently selected filter values */
  value: string[];
  /** Callback invoked when filter selection changes with new array of selected values */
  onChange: (value: string[]) => void;
}

/**
 * Multi-select dropdown component for KYC approval filters.
 * Allows selecting multiple approval status criteria with checkboxes.
 *
 * Available filter options:
 * - pending_approval_1: Files awaiting first-level approval
 * - pending_approval_2: Files awaiting second-level approval
 * - missing_documents: Document groups with no files
 * - pending_kyc_approval: All files approved but KYC process approval pending
 * - missing_compliance_document: Compliance document PDF has not been generated yet
 * - rejected_approval_2: At least one file rejected by C.O. (approval_2 = false)
 *
 * @example
 * ```tsx
 * <ApprovalFilterDropdown
 *   value={filters.approvalFilter}
 *   onChange={setters.setApprovalFilter}
 * />
 * ```
 */
export function ApprovalFilterDropdown({
  value,
  onChange,
}: ApprovalFilterDropdownProps) {
  const { t } = useTranslation();

  // Filter options based on API documentation
  const filterOptions = useMemo(
    () => [
      {
        value: 'pending_approval_1',
        label: t('kyc.filters.approvalFilter.options.pendingApproval1'),
      },
      {
        value: 'pending_approval_2',
        label: t('kyc.filters.approvalFilter.options.pendingApproval2'),
      },
      {
        value: 'missing_documents',
        label: t('kyc.filters.approvalFilter.options.missingDocuments'),
      },
      {
        value: 'pending_kyc_approval',
        label: t('kyc.filters.approvalFilter.options.pendingKycApproval'),
      },
      {
        value: 'missing_compliance_document',
        label: t(
          'kyc.filters.approvalFilter.options.missingComplianceDocument'
        ),
      },
      {
        value: 'rejected_approval_2',
        label: t('kyc.filters.approvalFilter.options.rejectedApproval2'),
      },
    ],
    [t]
  );

  // Handle checkbox toggle - memoized to prevent unnecessary re-renders
  const handleToggle = useCallback(
    (optionValue: string) => {
      const newValue = value.includes(optionValue)
        ? value.filter(v => v !== optionValue)
        : [...value, optionValue];
      onChange(newValue);
    },
    [value, onChange]
  );

  // Display text for the button
  const displayText = useMemo(() => {
    if (value.length === 0) {
      return t('kyc.filters.approvalFilter.label');
    }
    if (value.length === 1) {
      const option = filterOptions.find(opt => opt.value === value[0]);
      return option?.label || t('kyc.filters.approvalFilter.label');
    }
    return t('kyc.filters.approvalFilter.selectedCount', {
      count: value.length,
    });
  }, [value, filterOptions, t]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant='outline'
          className='min-w-[200px] justify-between'
          aria-label={t('kyc.filters.approvalFilter.ariaLabel')}
        >
          <span className='truncate'>{displayText}</span>
          {value.length > 0 && (
            <span className='ml-2 rounded-full bg-primary px-2 py-0.5 text-xs text-primary-foreground'>
              {value.length}
            </span>
          )}
          <ChevronDown className='ml-2 h-4 w-4 shrink-0' />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='start' className='w-[280px]'>
        <DropdownMenuLabel>
          {t('kyc.filters.approvalFilter.label')}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {filterOptions.map(option => (
          <DropdownMenuCheckboxItem
            key={option.value}
            checked={value.includes(option.value)}
            onCheckedChange={() => handleToggle(option.value)}
            className='cursor-pointer'
          >
            {option.label}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
